/**
 * Settings > Case Preparation > Firm Instructions: how the firm works and
 * writes, in one place, read by every agent at the firm before it starts on
 * a case.
 *
 * Everyone at the firm sees the text; owners and admins edit it. Behind the
 * scenes it is the firm's `AGENTS.md`, mounted verbatim into every agent
 * workspace, but nothing here says so: to the firm it is a settings section
 * like the others.
 */
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { toast } from "sonner";

import { LawFirmsAPI } from "@/api/lawFirms";
import type { FirmAgentInstructionsDTO } from "@/api/types/lawFirm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { CasePrepSection } from "./casePrepParts";

type Props = {
  lawFirmId: string | null;
  /** Owner / admin (or platform superadmin): may save. */
  canEdit: boolean;
  /** Shown in the caption; falls back to "your firm". */
  firmName?: string | null;
};

/** Written for an immigration practice, so the empty state already suggests what belongs here. */
export const PLACEHOLDER = [
  "e.g. Address USCIS officers formally. Never state a filing date the file does not show.",
  "Refer to the beneficiary by surname after the first mention.",
  'Cite exhibits as "Exhibit 4", never "Ex. 4".',
  "Flag any evidence older than five years for the attorney."
].join(" ");

/** Headings a click drops into an empty field, to write under. */
export const STARTERS: ReadonlyArray<{ label: string; heading: string }> = [
  { label: "Tone and Voice", heading: "Tone and voice" },
  {
    label: "What to Check Before Anything Is Sent",
    heading: "Before anything is sent"
  },
  { label: "Citations and Exhibits", heading: "Citations and exhibits" },
  { label: "Who Signs What", heading: "Who signs what" }
];

/** Fallback cap until the server tells us its own. */
const DEFAULT_MAX_CHARS = 40_000;
/** The counter only appears when the text is within this many characters of the cap. */
const COUNTER_WITHIN = 2_000;

/** "just now", "12 minutes ago", "yesterday", "Mar 4" — the footer's clock. */
export const relativeLabel = (
  iso: string | null | undefined,
  now: number = Date.now()
): string | null => {
  if (!iso) return null;
  const stamp = new Date(iso).getTime();
  if (Number.isNaN(stamp)) return null;
  const minutes = Math.round((now - stamp) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60)
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(stamp).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year:
      new Date(stamp).getFullYear() === new Date(now).getFullYear()
        ? undefined
        : "numeric"
  });
};

/** The footer's "Updated … by …" line. */
export const updatedLabel = (
  saved: Pick<
    FirmAgentInstructionsDTO,
    "updated_at" | "updated_by_name"
  > | null,
  now: number = Date.now()
): string => {
  const when = relativeLabel(saved?.updated_at, now);
  if (!when) return "Not set yet.";
  return saved?.updated_by_name
    ? `Updated ${when} by ${saved.updated_by_name}`
    : `Updated ${when}`;
};

/** Append a heading to the draft, keeping a blank line between blocks. */
export const withStarter = (draft: string, heading: string): string => {
  const base = draft.trimEnd();
  const block = `## ${heading}\n- `;
  return base ? `${base}\n\n${block}` : block;
};

export const FirmInstructionsSection: FC<Props> = ({
  lawFirmId,
  canEdit,
  firmName
}) => {
  const [saved, setSaved] = useState<FirmAgentInstructionsDTO | null>(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(Boolean(lawFirmId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!lawFirmId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    LawFirmsAPI.getAgentInstructions(lawFirmId)
      .then((data) => {
        if (cancelled) return;
        setSaved(data);
        setDraft(data.text ?? "");
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSaved(null);
        setError(
          err instanceof Error
            ? err.message
            : "Could not load firm instructions."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lawFirmId, reloadKey]);

  const base = saved?.text ?? "";
  const dirty = draft !== base;
  const maxChars = saved?.max_chars ?? DEFAULT_MAX_CHARS;
  const tooLong = draft.length > maxChars;
  const showCounter = draft.length >= maxChars - COUNTER_WITHIN;
  // Until the text has loaded there is nothing to edit: an empty box saved
  // after a failed load would wipe every agent's instructions.
  const loaded = saved !== null && !error;
  const editable =
    canEdit && loaded && !saving && !loading && Boolean(lawFirmId);
  const who = firmName?.trim() || "your firm";

  const footer = useMemo(
    () => (loaded ? updatedLabel(saved) : ""),
    [loaded, saved]
  );

  const save = async () => {
    if (!lawFirmId || !loaded) return;
    const previous = draft;
    setSaving(true);
    try {
      const updated = await LawFirmsAPI.updateAgentInstructions(
        lawFirmId,
        draft.trim(),
        saved?.version
      );
      setSaved(updated);
      setDraft(updated.text ?? "");
      toast.success("Saved. Every agent at the firm follows this from now on.");
    } catch (err) {
      setDraft(previous);
      toast.error(
        err instanceof Error ? err.message : "Could not save firm instructions."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CasePrepSection
      title="Firm Instructions"
      description={`How your firm works and writes. Every agent at ${who} reads this before it starts on a case.`}
      testId="firm-instructions"
    >
      {error ? (
        <div
          className="flex items-center justify-between gap-4 px-4 py-3"
          data-testid="firm-instructions-error"
        >
          <span className="text-sm text-danger">{error}</span>
          <Button
            variant="secondary"
            size="sm"
            disabled={loading}
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Retry
          </Button>
        </div>
      ) : loading ? (
        <div className="px-4 py-3" data-testid="firm-instructions-loading">
          <Skeleton className="h-56 w-full bg-fill-2" />
        </div>
      ) : (
        <div className="flex w-full flex-col gap-3 px-4 py-3">
          <Textarea
            aria-label="Firm Instructions"
            data-testid="firm-instructions-text"
            value={draft}
            placeholder={PLACEHOLDER}
            readOnly={!canEdit}
            disabled={saving || (canEdit && !loaded)}
            aria-invalid={tooLong || undefined}
            rows={10}
            className={cn(
              "min-h-56 text-base leading-6",
              !canEdit && "cursor-default bg-fill-1 opacity-100"
            )}
            onChange={(event) => setDraft(event.target.value)}
          />

          {editable && draft.trim() === "" ? (
            <div
              className="flex flex-wrap items-center gap-1.5"
              data-testid="firm-instructions-starters"
            >
              <span className="text-sm text-fg-3">Start With</span>
              {STARTERS.map((starter) => (
                <Button
                  key={starter.label}
                  variant="pill"
                  size="xs"
                  className="px-2.5"
                  onClick={() =>
                    setDraft((current) => withStarter(current, starter.heading))
                  }
                >
                  {starter.label}
                </Button>
              ))}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span
              className="text-sm text-fg-3"
              data-testid="firm-instructions-footer"
            >
              {canEdit
                ? footer
                : [footer, "Only firm owners and admins can change this."]
                    .filter(Boolean)
                    .join(" ")}
            </span>
            <div className="flex items-center gap-3">
              {showCounter ? (
                <span
                  className={cn(
                    "text-sm",
                    tooLong ? "text-danger" : "text-fg-3"
                  )}
                >
                  {draft.length.toLocaleString()} / {maxChars.toLocaleString()}
                </span>
              ) : null}
              {canEdit ? (
                <>
                  {dirty ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={saving}
                      onClick={() => setDraft(base)}
                    >
                      Discard
                    </Button>
                  ) : null}
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!editable || !dirty || tooLong}
                    onClick={() => void save()}
                    data-testid="save-firm-instructions"
                  >
                    {saving ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : null}
                    Save
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </CasePrepSection>
  );
};
